import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import analyticsService from '../../services/analytics';

const AnalyticsDashboard = () => {
    const [stats, setStats] = useState(null);
    const [trends, setTrends] = useState(null);
    const [topArticles, setTopArticles] = useState([]);
    const [topVideos, setTopVideos] = useState([]);
    const [activeUsers, setActiveUsers] = useState(0);
    const [period, setPeriod] = useState('today');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
        const interval = setInterval(() => {
            fetchActiveUsers();
        }, 30000); // Update every 30 seconds
        return () => clearInterval(interval);
    }, [period]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [statsRes, trendsRes, articlesRes, videosRes, activeRes] = await Promise.all([
                api.get(`/analytics/stats?period=${period}`),
                api.get('/analytics/trends'),
                api.get('/analytics/top-content?type=articles&limit=5'),
                api.get('/analytics/top-content?type=videos&limit=5'),
                api.get('/analytics/active-users')
            ]);

            setStats(statsRes.data);
            setTrends(trendsRes.data);
            setTopArticles(articlesRes.data);
            setTopVideos(videosRes.data);
            setActiveUsers(activeRes.data.activeUsers);
        } catch (error) {
            console.error('Error fetching analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchActiveUsers = async () => {
        try {
            const res = await api.get('/analytics/active-users');
            setActiveUsers(res.data.activeUsers);
        } catch (error) {
            console.error('Error fetching active users:', error);
        }
    };

    if (loading) {
        return <div className="loader"><div className="loader-spinner"></div></div>;
    }

    return (
        <div>
            {/* Active Users Card */}
            <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '24px',
                color: '#fff'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                    <div>
                        <div style={{ fontSize: '14px', opacity: 0.9 }}>LIVE ACTIVE USERS</div>
                        <div style={{ fontSize: '48px', fontWeight: 'bold' }}>{activeUsers}</div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Currently browsing</div>
                    </div>
                    <div style={{ fontSize: '48px' }}>👥</div>
                </div>
            </div>

            {/* Period Selector */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {['today', 'week', 'month'].map(p => (
                    <button
                        key={p}
                        onClick={() => setPeriod(p)}
                        style={{
                            padding: '8px 20px',
                            background: period === p ? 'var(--red)' : '#f0f0f0',
                            color: period === p ? '#fff' : '#333',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontWeight: '600'
                        }}
                    >
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                ))}
            </div>

            {/* Stats Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', textAlign: 'center', borderTop: '4px solid var(--red)' }}>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--navy)' }}>{stats?.totalViews?.toLocaleString() || 0}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>Total Views</div>
                </div>
                <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', textAlign: 'center', borderTop: '4px solid var(--navy)' }}>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--navy)' }}>{stats?.uniqueVisitors?.toLocaleString() || 0}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>Unique Visitors</div>
                </div>
                <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', textAlign: 'center', borderTop: '4px solid #e85d04' }}>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--navy)' }}>{stats?.articleViews?.toLocaleString() || 0}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>Article Views</div>
                </div>
                <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', textAlign: 'center', borderTop: '4px solid #1a7431' }}>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--navy)' }}>{stats?.videoViews?.toLocaleString() || 0}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>Video Views</div>
                </div>
            </div>

            {/* Trends Card */}
            {trends && (
                <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', marginBottom: '24px', boxShadow: '0 1px 6px rgba(0,0,0,.08)' }}>
                    <h3 style={{ marginBottom: '20px' }}>📈 View Trends</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--navy)' }}>{trends.today?.toLocaleString() || 0}</div>
                            <div style={{ fontSize: '12px', color: '#888' }}>Today</div>
                            {trends.todayVsYesterday !== undefined && (
                                <div style={{ fontSize: '11px', color: trends.todayVsYesterday >= 0 ? 'green' : 'red' }}>
                                    {trends.todayVsYesterday >= 0 ? '↑' : '↓'} {Math.abs(trends.todayVsYesterday)} vs yesterday
                                </div>
                            )}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--navy)' }}>{trends.yesterday?.toLocaleString() || 0}</div>
                            <div style={{ fontSize: '12px', color: '#888' }}>Yesterday</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--navy)' }}>{trends.thisMonth?.toLocaleString() || 0}</div>
                            <div style={{ fontSize: '12px', color: '#888' }}>This Month</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Category Stats */}
            {stats?.categoryStats && Object.keys(stats.categoryStats).length > 0 && (
                <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', marginBottom: '24px', boxShadow: '0 1px 6px rgba(0,0,0,.08)' }}>
                    <h3 style={{ marginBottom: '20px' }}>📊 Views by Category</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                        {Object.entries(stats.categoryStats).map(([category, count]) => (
                            count > 0 && (
                                <div key={category} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                                    <span>{category}</span>
                                    <span style={{ fontWeight: 'bold', color: 'var(--navy)' }}>{count.toLocaleString()}</span>
                                </div>
                            )
                        ))}
                    </div>
                </div>
            )}

            {/* Device Stats */}
            {stats?.devices && (
                <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', marginBottom: '24px', boxShadow: '0 1px 6px rgba(0,0,0,.08)' }}>
                    <h3 style={{ marginBottom: '20px' }}>📱 Device Breakdown</h3>
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--navy)' }}>{stats.devices.mobile?.toLocaleString() || 0}</div>
                            <div style={{ fontSize: '12px', color: '#888' }}>📱 Mobile</div>
                        </div>
                        <div style={{ flex: 1, textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--navy)' }}>{stats.devices.desktop?.toLocaleString() || 0}</div>
                            <div style={{ fontSize: '12px', color: '#888' }}>💻 Desktop</div>
                        </div>
                        <div style={{ flex: 1, textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--navy)' }}>{stats.devices.tablet?.toLocaleString() || 0}</div>
                            <div style={{ fontSize: '12px', color: '#888' }}>📟 Tablet</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Top Content */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Top Articles */}
                <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 6px rgba(0,0,0,.08)' }}>
                    <h3 style={{ marginBottom: '20px' }}>📰 Top Articles</h3>
                    {topArticles.map((article, idx) => (
                        <div key={article._id} style={{ padding: '10px 0', borderBottom: '1px solid #eee' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <span style={{ fontWeight: 'bold', color: 'var(--red)', marginRight: '10px' }}>#{idx + 1}</span>
                                    <span>{article.title?.substring(0, 50)}...</span>
                                </div>
                                <span style={{ fontWeight: 'bold', color: 'var(--navy)' }}>{article.views?.toLocaleString()}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Top Videos */}
                <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 6px rgba(0,0,0,.08)' }}>
                    <h3 style={{ marginBottom: '20px' }}>🎬 Top Videos</h3>
                    {topVideos.map((video, idx) => (
                        <div key={video._id} style={{ padding: '10px 0', borderBottom: '1px solid #eee' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <span style={{ fontWeight: 'bold', color: 'var(--red)', marginRight: '10px' }}>#{idx + 1}</span>
                                    <span>{video.title?.substring(0, 50)}...</span>
                                </div>
                                <span style={{ fontWeight: 'bold', color: 'var(--navy)' }}>{video.views?.toLocaleString()}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AnalyticsDashboard;